import React, { useEffect, useMemo, useRef, useContext } from 'react'
import { Button, Carousel, Divider, List, Space, Spin, message, Row, Col } from 'antd'
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { ACCEPT, DECLINE, UNKNOWN_DOC } from '../../../../Constants'
import DocumentStatus from './DocumentStatus'
import './PartnerDocuments.css'
import { useDocumentTypesQuery } from '../../../../api/react-query/dict'
import { profileScreen } from '../../../../translates'
import { adminReducerActions, partnerStatusCodes } from '../../../../AppConfig'
import UploadDocumentButton from './UploadDocumentButton'
import DocumentPreview from './DocumentPreview'
import { ConfigContext } from '../../../../context/configContext'
import { useGetPartnerId } from '../../../../hooks/useGetPartnerId'
import AutoFocus from '../Assets/AutoFocus'
import ApprovalAction from '../../Approval/ApprovalAction'

function PartnerDocuments ({
  profile,
  documents,
  loading,
  onCommit,
  getDocumentStatus,
  getDocumentButton,
  correctForm
}) {
  const id = useGetPartnerId()
  const {
    isSupervisor,
    isPersonalManager,
    isSecurity,
    isContentCoordinator,
    isResearcherCoordinator,
    isOperatorCoordinator,
    isMentorCoordinator,
    isExternalCoordinator,
    setConfig
  } = useContext(ConfigContext)
  const slider = useRef(null)
  const handleCommit = (d, o, c) => onCommit(d, o, c, () => slider?.current?.next())

  const iconStyle = { fontSize: '40px' }
  const buttonStyle = { height: '64px' }

  const { data, isLoading, isError } = useDocumentTypesQuery()
  const documentsTypes = useMemo(() => Object.fromEntries(data?.result?.map(item => ([item.id, item.name])) || []), [data])

  useEffect(() => {
    if (isError || data?.errorMessage) {
      message.error('Не удалось получить типы документов')
    }
  }, [isError, data])

  const dataSource = useMemo(() => documents
    .filter(d => profile?.[d]?.fileId || d === profileScreen.input.vaccinacionPhoto.name)
    .filter(d => d !== profileScreen.input.vaccinacionPhoto.name || (profile.statusCode !== partnerStatusCodes.archive || profile.vaccinacionPhoto?.fileId))
    .filter( // вакцину показываем для всех кто не СВ, ПМ, СБ, KK. для СВ или ПМ или СБ или КК показываем только если есть сам файл
      d => d !== profileScreen.input.vaccinacionPhoto.name || (
        (
          d === profileScreen.input.vaccinacionPhoto.name &&
          !isSupervisor &&
          !isPersonalManager &&
          !isSecurity &&
          !isContentCoordinator &&
          !isResearcherCoordinator &&
          !isOperatorCoordinator &&
          !isMentorCoordinator
        ) || (
          d === profileScreen.input.vaccinacionPhoto.name &&
          (isSupervisor || isPersonalManager || isSecurity || isContentCoordinator || isResearcherCoordinator || isOperatorCoordinator || isMentorCoordinator) &&
          profile?.[d]?.fileId
        )
      )
    )
    .map(d => ({ id: d, name: documentsTypes[d] ?? UNKNOWN_DOC, status: getDocumentStatus?.(d) }))
  , [
    documents,
    documentsTypes,
    getDocumentStatus,
    isContentCoordinator,
    isOperatorCoordinator,
    isMentorCoordinator,
    isPersonalManager,
    isResearcherCoordinator,
    isSecurity,
    isSupervisor,
    profile
  ])

  if (isLoading) {
    return <Spin spinning />
  }

  const goToSlide = index => {
    slider.current.goTo(index)
  }

  return (
    <>
      <AutoFocus />
      <Space direction='vertical' className='PartnerDocuments'>
        {dataSource?.length > 0 &&
          <List
            size='small'
            bordered
            dataSource={dataSource}
            renderItem={(item, index) => (
              <List.Item>
                <Row align='middle' justify='space-between' className='w-100'>
                  <Col>
                    {
                      (item.id !== profileScreen.input.vaccinacionPhoto.name || profile?.[item.id]?.fileId) &&
                        <DocumentStatus status={item.status} />
                    }
                    <Button
                      className='ml-2'
                      style={{ marginLeft: 6 }}
                      type='text'
                      onClick={() => goToSlide(index)}
                    >
                      {item.name}
                    </Button>
                  </Col>
                  {
                    id &&
                    item.id === profileScreen.input.vaccinacionPhoto.name &&
                    !getDocumentButton &&
                    !isSupervisor &&
                    !isPersonalManager &&
                    !isSecurity &&
                    !isExternalCoordinator &&
                      <Col>
                        <UploadDocumentButton
                          document={item.id}
                          name={profile?.[item.id]?.fileId ? 'Обновить' : 'Загрузить'}
                          uploadUrl={`admin/partners/${id}/document/vaccinacionPhoto`}
                          onSuccess={data =>
                            setConfig({
                              type: adminReducerActions.updatePartner,
                              value: data.partner
                            })}
                          onError={(error) => message.error(error)}
                        />
                      </Col>
                  }
                  {
                    getDocumentButton &&
                      <Col>
                        {
                          getDocumentButton(item.id)
                        }
                      </Col>
                  }
                </Row>
              </List.Item>
            )}
          />}
        <Carousel ref={slider}>
          {
            documents.filter(d => profile?.[d]?.fileId).map(d => (
              <div key={d}>
                <h2>{documentsTypes[d] ?? UNKNOWN_DOC}</h2>
                <DocumentPreview
                  document={profile?.[d]}
                  title={documentsTypes[d] ?? UNKNOWN_DOC}
                  documentType={d}
                  correctForm={correctForm}
                />
                <div>
                  <Button style={buttonStyle} onClick={() => slider?.current?.prev()} type='link'>
                    <ArrowLeftOutlined style={iconStyle} />
                  </Button>
                  <Button style={buttonStyle} onClick={() => slider?.current?.next()} type='link'>
                    <ArrowRightOutlined style={iconStyle} />
                  </Button>
                </div>
                {profile?.[d]?.allowReview &&
                  <>
                    <Divider />
                    <ApprovalAction
                      targetType={d}
                      disabled={false}
                      defaultComment={profile?.[d]?.statusReason}
                      defaultOutcome={profile?.[d]?.status}
                      onAccept={() => handleCommit(d, ACCEPT)}
                      onDecline={(comment) => handleCommit(d, DECLINE, comment)}
                    />
                  </>}
              </div>
            ))
          }
        </Carousel>
      </Space>
    </>
  )
}

export default PartnerDocuments
